import { CloseCircleFilled, CloudUploadOutlined, LoadingOutlined } from "@ant-design/icons";
import { message, Upload } from "antd";
import React, { useState } from "react";

import { getDocumentPath, useUploadFile } from "../../../api/v1";

import { AmenityIconImage } from "./AmenityIconImage";
import { CatalogueId } from "./types";

import type { RcFile, UploadProps } from "antd/es/upload";

const MAX_FILE_SIZE_MB = 1;

const MARKETING_UPLOAD_ROOT = "marketing";

const slugifyPathSegment = (value?: string) =>
  (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildUploadPath = (organizationName: string | undefined, catalogueId: CatalogueId) =>
  [slugifyPathSegment(organizationName), MARKETING_UPLOAD_ROOT, slugifyPathSegment(catalogueId)]
    .filter(Boolean)
    .join("/");

export type AmenityIconUploadProps = {
  value?: string | null;
  onChange: (value: string) => void;
  catalogueId: CatalogueId;
  organizationName?: string;
};

export const AmenityIconUpload = (props: AmenityIconUploadProps) => {
  const { value, onChange, catalogueId, organizationName } = props;
  const { uploadFileAsync, isUploadingFile } = useUploadFile();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const beforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files");
      return Upload.LIST_IGNORE;
    }

    const isWithinSizeLimit = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
    if (!isWithinSizeLimit) {
      message.error(`Image must be smaller than ${MAX_FILE_SIZE_MB}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest: UploadProps["customRequest"] = async (options) => {
    const file = options.file as RcFile;
    const path = buildUploadPath(organizationName, catalogueId);

    try {
      const result = await uploadFileAsync({ file, path, isSigned: false });
      const docPath = getDocumentPath(result);
      onChange(docPath);
      setPreviewUrl(result.documentUrl);
      options.onSuccess?.(result);
    } catch {
      message.error("Image upload failed. Please try again");
      options.onError?.(new Error("Upload failed"));
    }
  };

  const isBusy = isUploadingFile;
  const imageSrc = previewUrl ?? value;

  if (value && imageSrc) {
    return (
      <div style={{ position: "relative", width: 96, height: 96 }}>
        <AmenityIconImage
          src={imageSrc}
          alt="Amenity icon"
          size={96}
          borderRadius={8}
          style={{ border: "1px solid #D9D9D9" }}
        />
        <CloseCircleFilled
          onClick={() => {
            onChange("");
            setPreviewUrl(null);
          }}
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            fontSize: 16,
            color: "rgba(0, 0, 0, 0.65)",
            background: "#fff",
            borderRadius: "50%",
            cursor: "pointer",
            zIndex: 2,
          }}
        />
      </div>
    );
  }

  return (
    <Upload
      showUploadList={false}
      accept="image/*"
      maxCount={1}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
      disabled={isBusy}
    >
      <div
        style={{
          width: 96,
          height: 96,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          border: "1px dashed #D9D9D9",
          borderRadius: 8,
          cursor: isBusy ? "not-allowed" : "pointer",
          background: "#FAFAFA",
        }}
      >
        {isBusy ? <LoadingOutlined style={{ fontSize: 20 }} /> : <CloudUploadOutlined style={{ fontSize: 20 }} />}
        <span style={{ fontSize: 13, fontWeight: 500 }}>Upload</span>
        <span style={{ fontSize: 11, color: "#8C8C8C" }}>Max {MAX_FILE_SIZE_MB}MB</span>
      </div>
    </Upload>
  );
};
